/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  SLRPrompt,
  SLRPromptProps,
  SLRSpinner,
  SLRTable,
  TableColumn,
  getIcon,
  hasValue,
  showErrorToast
} from '@SLR/shared-library';
import { UserMembershipInfo } from '@SLR/user-service-full-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SIZE_PER_PAGE } from 'configs';
import {
  useGetAllMembersOfOrganization,
  useRemoveUserFromOrganization,
  useSetOrganizationRolesForUser
} from 'hooks';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { InviteModal, ListAction } from '.';

type MembersListProps = {
  organizationId?: string;
  isAdmin?: boolean;
};

export const MembersList: FC<MembersListProps> = ({
  organizationId,
  isAdmin
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'organizationsPage.details'
  });

  const {
    data: members,
    isPending,
    isError
  } = useGetAllMembersOfOrganization(organizationId ?? '');

  useEffect(() => {
    if (isError) {
      showErrorToast('toasts.loadOrganizationMembers.error');
    }
  }, [isError]);

  const { mutate: removeUser } = useRemoveUserFromOrganization();
  const { mutate: setRole } = useSetOrganizationRolesForUser();

  const [showInvitationModal, setShowInvitationModal] = useState(false);

  const [promptData, setPromptData] = useState<SLRPromptProps | null>(null);
  const hidePrompt = () => setPromptData(null);

  const getMember = useCallback(
    (id: string) => members?.find((m) => m.userId === id)?.userName,
    [members]
  );

  const handleRemove = useCallback(
    (userId: string) => {
      setPromptData({
        confirmButtonVariant: 'danger',
        labels: t('removeMember', {
          returnObjects: true,
          name: getMember(userId)
        }),

        onConfirm: () => {
          if (organizationId) {
            removeUser({ organizationId, userId });
          }
          hidePrompt();
        },
        onCancel: hidePrompt
      });
    },
    [getMember, organizationId, removeUser, t]
  );

  const handleUpdateRole = useCallback(
    (userId: string, role: string) => {
      setPromptData({
        labels: t('updateRole', {
          returnObjects: true,
          name: getMember(userId),
          role: t(`${role as 'admin' | 'member'}`)
        }),
        onConfirm: () => {
          if (organizationId) {
            // eslint-disable-next-line
            // @ts-ignore
            setRole({ organizationId, userId, requestBody: [role] });
          }
          hidePrompt();
        },
        onCancel: hidePrompt
      });
    },
    [getMember, organizationId, setRole, t]
  );

  const columns: TableColumn<
    UserMembershipInfo & { userName: string; role: string }
  >[] = useMemo(() => {
    const cols = [
      {
        label: t('name'),
        accessor: 'userName',
        sortable: true
      },
      {
        label: t('role'),
        accessor: 'role',
        cellFn: ({ value }: { value: 'admin' | 'member' }) => t(`${value}`)
      }
    ] as TableColumn<UserMembershipInfo & { userName: string; role: string }>[];

    if (isAdmin) {
      cols.splice(1, 0, {
        label: t('email'),
        accessor: 'emailAddress',
        sortable: true
      });

      cols.push({
        label: (
          <>
            <div className="d-none" aria-hidden>
              {t('listOptions')}
            </div>
          </>
        ),
        accessor: 'userId',
        cellFn: ({ data }) => (
          <ListAction
            id={data.userId}
            currentRole={data.role}
            onDelete={handleRemove}
            onRoleChange={handleUpdateRole}
          />
        )
      });
    }

    return cols;
  }, [t, isAdmin, handleRemove, handleUpdateRole]);

  if (isPending) return <SLRSpinner height="5rem" />;

  return (
    <>
      {promptData && <SLRPrompt {...promptData} />}
      {isAdmin && organizationId && showInvitationModal && (
        <InviteModal
          organizationId={organizationId}
          onClose={() => setShowInvitationModal(false)}
        />
      )}
      {hasValue(members) && (
        <Container className="table-responsive-md py-6">
          <SLRTable<UserMembershipInfo & { userName: string; role: string }>
            responsive="md"
            columns={columns}
            data={members}
            pageSize={SIZE_PER_PAGE}
            maxPages={5}
            enablePagination
            sortIcon={getIcon('fal', 'long-arrow-up')}
            header={{
              title: t('memberCount', {
                count: members?.length ?? 0
              }),
              children: isAdmin ? (
                <Button
                  variant="outline-primary"
                  size="lg"
                  onClick={() => setShowInvitationModal(true)}
                >
                  <FontAwesomeIcon
                    icon={getIcon('fal', 'plus')}
                    size="lg"
                    className="me-2"
                  />
                  {t('inviteUser')}
                </Button>
              ) : undefined
            }}
            initialSort={{
              accessor: 'firstName',
              sortOrder: 'asc'
            }}
          />
        </Container>
      )}
    </>
  );
};
